import React, { useState, useEffect } from 'react'
import { Layout, Button, message, Modal, Tabs } from 'antd'
import { LogoutOutlined, PlusOutlined, UserOutlined } from '@ant-design/icons'
import { useAuth } from '../context/AuthContext'
import CustomerList from './CustomerList'
import AddCustomerForm from './AddCustomerForm'
import RoutePlanning from './RoutePlanning'
import AdminDashboard from './AdminDashboard'
import axios from 'axios'

const { Header, Content } = Layout

function Dashboard() {
  const { user, logout } = useAuth()
  const [customers, setCustomers] = useState([])
  const [addModalVisible, setAddModalVisible] = useState(false)
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('customers')
  const [dateFilter, setDateFilter] = useState('all')
  
  // 如果是管理员，默认显示管理员界面
  React.useEffect(() => {
    if (user?.role === 'admin') {
      setActiveTab('admin')
    }
  }, [user])

  useEffect(() => {
    fetchCustomers()
  }, [dateFilter])

  const fetchCustomers = async (filter = dateFilter) => {
    setLoading(true)
    try {
      const params = {}
      if (filter !== 'all') {
        params.status = 'completed'
        params.date_filter = filter
      }
      
      const response = await axios.get('/api/customers', { params })
      // 后端返回的是分页格式，需要提取customers数组
      setCustomers(response.data.customers || [])
    } catch (error) {
      console.error('获取客户列表失败:', error)
      message.error('获取客户列表失败')
    }
    setLoading(false)
  }

  const handleDateFilterChange = (filter) => {
    setDateFilter(filter)
  }

  const handleLogout = () => {
    logout()
    message.success('已退出登录')
  }

  const handleAddCustomer = async (values) => {
    try {
      await axios.post('/api/customers', values)
      message.success('客户添加成功')
      setAddModalVisible(false)
      fetchCustomers()
    } catch (error) {
      message.error('添加客户失败')
    }
  }

  const handleCompleteCustomer = async (customerId, completeData) => {
    try {
      const completeInfo = {
        customer_id: customerId,
        completed_remark: completeData.remark || '',
        completed_time: completeData.completed_time ? completeData.completed_time.format('YYYY-MM-DD') : null,
        new_phone_number: completeData.new_phone_number || '',
        completed_name: completeData.completed_name || ''
      }
      
      await axios.post('/api/customer/complete', completeInfo)
      message.success('业务办理完成')
      fetchCustomers()
    } catch (error) {
      console.error('办理完成错误:', error)
      if (error.response?.data?.error) {
        message.error(error.response.data.error)
      } else {
        message.error('办理失败')
      }
    }
  }

  // 构建标签页配置
  const getTabItems = () => {
    const baseTabs = [
      {
        key: 'customers',
        label: '客户管理',
        children: (
          <>
            <div style={{ marginBottom: 16, textAlign: 'right' }}>
              <Button 
                type="primary" 
                icon={<PlusOutlined />}
                onClick={() => setAddModalVisible(true)}
              >
                添加客户
              </Button>
            </div>
            <CustomerList 
              customers={customers} 
              loading={loading}
              onComplete={handleCompleteCustomer}
              dateFilter={dateFilter}
              onDateFilterChange={handleDateFilterChange}
            />
          </>
        ),
      },
      {
        key: 'route',
        label: '智能路线规划',
        children: (
          <RoutePlanning 
            customers={customers}
            onRouteUpdate={fetchCustomers}
          />
        ),
      },
    ]
    
    // 如果是管理员，添加管理员标签页
    if (user?.role === 'admin') {
      baseTabs.unshift({
        key: 'admin',
        label: '管理员控制台',
        children: <AdminDashboard />,
      })
    }
    
    return baseTabs
  }

  const tabItems = getTabItems()

  return (
    <Layout className="layout-container">
      <Header className="header">
        <div style={{ fontSize: '20px', fontWeight: 'bold' }}>
          销路通
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <span>
            <UserOutlined /> {user?.username}
          </span>
          <Button 
            type="text" 
            icon={<LogoutOutlined />} 
            onClick={handleLogout}
            style={{ color: 'white' }}
          >
            退出
          </Button>
        </div>
      </Header>
      
      <Content className="content">
        <Tabs 
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
        />
        
        <Modal
          title="添加新客户"
          open={addModalVisible}
          onCancel={() => setAddModalVisible(false)}
          footer={null}
          width={600}
        >
          <AddCustomerForm 
            onSubmit={handleAddCustomer}
            onCancel={() => setAddModalVisible(false)}
          />
        </Modal>
      </Content>
    </Layout>
  )
}

export default Dashboard